import {Component, Input} from "@angular/core";
import {UserService} from "../user/user.service";
import {SelectableUserModel} from "../types/selectable-user.model";
import {handleErrors} from "../misc/handle-errors.decorator";
import {UserSelectionService} from "./user-selection.service";
import {SearchHelpers} from "../helpers/search-helper";
import {ProjectService} from "../project/project.service";
import {PrivilegeViewService} from "../privilege-view/privilege-view.service";
import {ConfirmDialogService} from "../confirm-dialog/confirm-dialog-service";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  selector: "user-selection",
  templateUrl: "./users-selection.template.pug",
  providers: [UserService, ProjectService, ConfirmDialogService],
}) export class UserSelectionComponent {

  users: SelectableUserModel[] = [];
  filteredUsers: SelectableUserModel[] = [];
  displayedUsers: SelectableUserModel[] = [];
  selectedUsers: SelectableUserModel[] = [];

  currentPage: number = DEFAULT_PAGE;
  private _pageSize: number = DEFAULT_PAGE_LIMIT;

  private _projectId: string;

  maxPages: number;
  searchText: string = "";
  showFedLlcStatus: boolean = false;

  @Input()
  disableAll: boolean = false;

  @Input()
  set projectId(projectId: string) {
    if (!projectId) {
      this._projectId = undefined;
      this.users = [];
      this.recalculateMaxPages();
      this.updateDisplay();
      this.updateComplete();
    } else if (this._projectId !== projectId) {
      this._projectId = projectId;
      this.loadData();
    }
  }

  get projectId(): string {
    return this._projectId;
  }

  error: Error;

  dirty: boolean = false;
  complete: boolean = false;

  @Input()
  set pageSize(size: any) {
    if (this._pageSize && this._pageSize !== +size) {
      this._pageSize = +size;
      this.updateDisplay();
    } else {
      this._pageSize = +size;
    }
  }

  get pageSize() {
    return this._pageSize;
  }

  updateDirty(): void {
    this.dirty = true;
    this.updateSelectedUsers();
  }

  private updateSelectedUsers(): void {
    this.selectedUsers = this.users.filter(it => it.selected);
  }

  private updateComplete(): void {
    this.complete = this.users.some(it => it.selected);
  }

  @handleErrors()
  async loadData(): Promise<void> {
    this.users = await this.userSelectionService.getUsersForProject(this.projectId);

    this.recalculateMaxPages();
    this.updateDisplay();
    this.updateComplete();
    this.showFedLlcStatus = this.privilegesService.getDomainView() === "fedLLC";
  }

  private updateDisplay() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.filteredUsers = this.users.filter((user) => {
      if (this.searchText) {
        const values = [
          user.id,
          user.firstName,
          user.lastName,
          user.emailAddress,
          user.roles,
          ...user.privileges,
        ];

        const searchFailures = SearchHelpers.tokenizeString(this.searchText) // split on spaces but consider "" and ''
          .map(v => SearchHelpers.stripQuotes(v)) // strip quotes on ends if present
          .map(v => SearchHelpers.cleanParam(v)) // sanitize to create regexp
          .map(v => new RegExp(v, "i")) // create regexp
          .map(re => values.some(v => re.test(v))) // test regexp against all asset values
          .filter(it => it === false) // don't care about success
          .length;

        if (searchFailures) return false;
      }

      return true;
    });

    this.displayedUsers = this.filteredUsers.slice(start, end);

    this.updateSelectedUsers();
    this.recalculateMaxPages();
  }

  private recalculateMaxPages() {
    this.maxPages = Math.ceil(this.filteredUsers.length / (+this.pageSize));
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.updateDisplay();
  }

  @handleErrors()
  async save(): Promise<boolean> {
    const userIds = this.users.filter(it => it.selected).map(it => it.id);

    if (this.showFedLlcStatus && this.users.filter(it => it.selected).find(it => it.fedLlcPrivilege === false)) {
      // show confirmation dialog
      const message = `One or more Users selected are not Fed LLC Authorized in TMC and will not have visibility of the project unless Authorized. Do you want to continue?`;
      const response = await this.confirmDialogService.confirm("Confirm", message, "No", "Yes");
      if (response) {
        const result = await this.userSelectionService.saveUsersForProject(this.projectId, userIds);

        if (result) this.dirty = false;

        return result;
      }
    } else {
      const result = await this.userSelectionService.saveUsersForProject(this.projectId, userIds);

      if (result) this.dirty = false;

      return result;
    }
  }

  constructor(
    private projectService: ProjectService,
    private userService: UserService,
    private userSelectionService: UserSelectionService,
    private privilegesService: PrivilegeViewService,
    private confirmDialogService: ConfirmDialogService,
  ) {}
}
